import { Controller } from "@hotwired/stimulus"
import { formatNumberForDisplay, parseLocaleNumber, parseUserInput } from "../helpers/number_formatting"

export default class extends Controller {
  static targets = [
    "quantity",
    "unitPrice",
    "taxCode",
    "totalExclTax",
    "totalInclTax",
    "totalExclTaxWrapper",
    "totalInclTaxWrapper"
  ]
  static values = {
    compoundedTaxRates: Array,
    taxRates: Object
  }

  connect() {
    this.toggleTotalInputs()

    if (!this.compoundedTaxRatesValue.includes(this.taxCodeValue)) {
      this.calculateTotalPrices()
    }

    this.formatExistingTotals()
  }

  calculateTotalPrices() {
    this.calculateTotalInclTax()
    this.calculateTotalExclTax()
  }

  // Private

  formatExistingTotals() {
    this.unitPriceTarget.value = formatNumberForDisplay(parseLocaleNumber(this.unitPriceTarget.value))
    this.quantityTarget.value = formatNumberForDisplay(parseLocaleNumber(this.quantityTarget.value))

    const excl = parseLocaleNumber(this.totalExclTaxTarget.value)
    if (excl !== null) this.totalExclTaxTarget.value = formatNumberForDisplay(excl)

    const incl = parseLocaleNumber(this.totalInclTaxTarget.value)
    if (incl !== null) this.totalInclTaxTarget.value = formatNumberForDisplay(incl)
  }

  calculateTotalExclTax() {
    let price = null

    if (this.unitPriceFormat === "ht") {
      if (!this.quantityValue || !this.unitPriceValue) {
        return this.totalExclTaxTarget.value = ""
      } else {
        price = this.quantityValue * this.unitPriceValue
      }
    } else {
      if (!this.quantityValue || !this.unitPriceValue || [null].concat(this.compoundedTaxRatesValue).includes(this.taxCodeValue)) {
        return this.totalExclTaxTarget.value = ""
      } else {
        price = (this.quantityValue * this.unitPriceValue / (1 + this.taxRate))
      }
    }

    this.totalExclTaxTarget.value = formatNumberForDisplay(price ?? 0)
  }

  calculateTotalInclTax() {
    let price = null

    if (this.unitPriceFormat === "ht") {
      if (!this.quantityValue || !this.unitPriceValue || [null].concat(this.compoundedTaxRatesValue).includes(this.taxCodeValue)) {
        return this.totalInclTaxTarget.value = ""
      } else {
        price = this.quantityValue * this.unitPriceValue * (1 + this.taxRate)
      }
    } else {
      if (!this.quantityValue || !this.unitPriceValue) {
        return this.totalInclTaxTarget.value = ""
      } else {
        price = this.quantityValue * this.unitPriceValue
      }
    }

    this.totalInclTaxTarget.value = formatNumberForDisplay(price ?? 0)
  }

  toggleTotalInputs() {
    this.enableAllTotalInputs()
    this.disableFileteredTotalInputs()
  }

  enableAllTotalInputs() {
    [this.totalExclTaxTarget, this.totalInclTaxTarget].forEach(input => input.disabled = false)
    if (this.hasTotalExclTaxWrapperTarget) this.totalExclTaxWrapperTarget.classList.remove("input-suffix__200")
    if (this.hasTotalInclTaxWrapperTarget) this.totalInclTaxWrapperTarget.classList.remove("input-suffix__200")
  }

  disableFileteredTotalInputs() {
    this.totalInputsToDisable.forEach(input => {
      input.disabled = true
      if (input === this.totalExclTaxTarget && this.hasTotalExclTaxWrapperTarget) {
        this.totalExclTaxWrapperTarget.classList.add("input-suffix__200")
      }
      if (input === this.totalInclTaxTarget && this.hasTotalInclTaxWrapperTarget) {
        this.totalInclTaxWrapperTarget.classList.add("input-suffix__200")
      }
    })
  }

  get totalInputsToDisable() {
    if (this.compoundedTaxRatesValue.includes(this.taxCodeValue)) {
      if (this.unitPriceFormat === "ht") {
        return [this.totalExclTaxTarget]
      } else {
        return [this.totalInclTaxTarget]
      }
    } else {
      return [this.totalExclTaxTarget, this.totalInclTaxTarget]
    }
  }

  get quantityValue() {
    return parseUserInput(this.quantityTarget.value) || 0
  }

  get unitPriceValue() {
    return parseUserInput(this.unitPriceTarget.value) || 0
  }

  get taxCodeValue() {
    return this.taxCodeTarget.value || null
  }

  get taxRate() {
    return +this.taxRatesValue[this.taxCodeValue]
  }

  get unitPriceFormat() {
    return this.data.get("unitPriceFormat")
  }
}
